/**
 * 中间件共用工具函数
 * 提供统一的登录检查、headers 设置、cookie 管理等功能
 */

import { NextRequest, NextResponse } from "next/server"

import { checkLoginStatus } from "./checkLoginStatus"

/**
 * 检查登录状态并返回错误信息
 * @param request - NextRequest 对象
 * @param authTokenKey - 认证 token 的 cookie key
 * @param apiHost - API 主机地址
 * @param defaultLocale - 默认语言
 * @returns 登录错误消息（如果登录失效）或空字符串
 */
export async function checkAuthStatus(
  request: NextRequest,
  authTokenKey: string,
  apiHost: string,
  defaultLocale: string,
  supportedLocales: readonly string[] = []
): Promise<string> {
  const authToken = request.cookies.get(authTokenKey)?.value

  if (!authToken || !apiHost) {
    return ""
  }

  // 从 URL 路径段解析 locale，与 next-intl middleware 保持一致；
  // 无法识别时回退到 defaultLocale。不再读 cookie，避免与 URL 不一致。
  const firstSegment = request.nextUrl.pathname.split("/")[1] ?? ""
  const language = supportedLocales.includes(firstSegment) ? firstSegment : defaultLocale

  const loginError = await checkLoginStatus(authToken, apiHost, language)

  return loginError ? loginError.msg : ""
}

/**
 * 准备传递给服务端组件的 Request Headers
 * @param request - 原始 NextRequest 对象
 * @param authErrorMsg - 登录错误消息（如果有）
 * @returns 包含自定义 headers 的 Headers 对象
 */
export function prepareRequestHeaders(request: NextRequest, authErrorMsg: string): Headers {
  const requestHeaders = new Headers(request.headers)
  requestHeaders.set("x-pathname", request.nextUrl.pathname)

  if (authErrorMsg) {
    requestHeaders.set("X-Auth-Error", encodeURIComponent(authErrorMsg))
  }

  return requestHeaders
}

/**
 * 清除认证 token cookie
 * @param response - NextResponse 对象
 * @param authTokenKey - 认证 token 的 cookie key
 */
export function clearAuthCookie(response: NextResponse, authTokenKey: string): void {
  response.cookies.delete(authTokenKey)
  response.cookies.set(authTokenKey, "", {
    domain: ".metal.com",
    path: "/",
    maxAge: 0,
  })
}

/**
 * 设置设备相关的 headers 和 cookie
 * @param response - NextResponse 对象
 * @param isMobile - 是否为移动设备
 */
export function setDeviceHeaders(response: NextResponse, isMobile: boolean): void {
  response.headers.set("Accept-CH", "Sec-CH-UA-Mobile, Viewport-Width")
  response.headers.set("Vary", "Sec-CH-UA-Mobile, Viewport-Width, User-Agent")
  response.cookies.set("x-device", isMobile ? "mobile" : "desktop", { path: "/", sameSite: "lax" })
}
